import { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { know, type InvitationInfo } from '../api/know'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { postLoginRoute } from '../auth/postLoginRoute'
import { AboutButton, VerSportsLogo } from '@versports/ui'

/**
 * Punto de aterrizaje de los links de invitación admin (`/invite/:token`).
 *
 * El frontend NO autentica con el token — solo resuelve QUÉ le piden al
 * destinatario y a dónde mandarlo. Si la invitación está vigente:
 *   - Si ya hay sesión y el email coincide, sigue por `postLoginRoute`, que
 *     antepone el gate de consentimiento si todavía no lo firmó (orden
 *     CONSENTIMIENTO → CUESTIONARIO, doc 32 §4) y si no manda directo a
 *     `/data/{code}`.
 *   - Si no hay sesión, manda a `/login` con el email pre-cargado y el
 *     cuestionario + club colgados en el query, para que el banner de
 *     `ManagerLoginForm` diga QUÉ le están pidiendo y DE PARTE DE QUIÉN en
 *     vez del copy genérico. El destino final (`next=/data/{code}`) lo
 *     vuelve a filtrar `postLoginRoute` después del login.
 *
 * Para tokens inválidos / usados / vencidos / revocados mostramos un mensaje
 * explicativo + un link al login normal, en vez de tirar 404 al usuario.
 */
export function InvitePage() {
  const { token = '' } = useParams<{ token: string }>()
  const { user, status } = useAuth()
  const [info, setInfo] = useState<InvitationInfo | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setInfo(null)
    setError(null)
    know.resolveInvitation(token)
      .then((data) => {
        if (!cancelled) setInfo(data)
      })
      .catch((e) => {
        if (cancelled) return
        const msg =
          e instanceof ApiError && e.status === 404
            ? 'Esta invitación no existe o ya fue revocada.'
            : e instanceof ApiError
              ? e.message
              : 'Error de red.'
        setError(msg)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  if (status === 'hydrating' || status === 'idle')
    return <Shell><p className="text-sm text-text-muted">Cargando…</p></Shell>

  if (error) {
    return (
      <Shell>
        <div className="banner-err">{error}</div>
        <p className="mt-md text-sm text-text-muted">
          ¿Era tu invitación? Pedile a quien te invitó que la reenvíe.
        </p>
        <p className="mt-md text-sm">
          <Link to="/login">Ir al login →</Link>
        </p>
      </Shell>
    )
  }

  if (info === null) {
    return <Shell><p className="text-sm text-text-muted">Cargando…</p></Shell>
  }

  if (info.status !== 'pending' && info.status !== 'used') {
    // expired / revoked / unknown — el resolver no nos da datos útiles para
    // proceder.
    return (
      <Shell>
        <h1 className="m-0">Invitación no disponible</h1>
        <p className="mt-md text-sm text-text-secondary">
          <InvitationSummary info={info} />
        </p>
        <p className="mt-md text-sm text-text-muted">
          {info.status === 'expired'
            ? 'Esta invitación venció.'
            : info.status === 'revoked'
              ? 'Esta invitación fue revocada por el admin.'
              : info.status === 'scheduled'
                ? 'Esta invitación todavía no está disponible.'
                : 'Estado desconocido.'}
        </p>
        <p className="mt-md text-sm">
          Si ya tenés cuenta, podés{' '}
          <Link to="/login">entrar al login</Link> y completar el cuestionario
          si todavía está disponible para vos.
        </p>
      </Shell>
    )
  }

  const next = `/data/${info.questionnaire_code}`

  // Si ya hay session con el email de la invitación → seguir la cadena de
  // gates (`postLoginRoute`): consentimiento primero si falta, cuestionario
  // después. Si la sesión es de otro mail (admin / coach testeando),
  // mostramos un mensaje en vez de loguearlo silenciosamente con otra
  // identidad.
  if (status === 'authenticated' && user) {
    if ((user.email ?? '').toLowerCase() === info.email.toLowerCase()) {
      return <Navigate to={postLoginRoute(user, next)} replace />
    }
    return (
      <Shell>
        <h1 className="m-0">Invitación para otro email</h1>
        <p className="mt-md text-sm text-text-secondary">
          <InvitationSummary info={info} />
        </p>
        <p className="mt-md text-sm text-text-muted">
          Esta invitación fue enviada a <strong>{info.email}</strong>, pero
          estás logueado como <strong>{user.email ?? user.username}</strong>.
        </p>
        <p className="mt-md text-sm">
          <Link to="/login">Cerrá sesión e ingresá con el otro email</Link>{' '}
          o pedí que te reenvíen la invitación al mail que usás.
        </p>
      </Shell>
    )
  }

  // No hay sesión — derivamos al login con email pre-completado + el
  // cuestionario y el club que motivan la invitación (los muestra el banner
  // de `ManagerLoginForm`). Nada de esto es dato sensible: el destinatario
  // ya tiene el mail y el link en su inbox.
  const params = new URLSearchParams({
    email: info.email,
    next,
    code: info.questionnaire_code,
    qname: info.questionnaire_name_es,
  })
  if (info.club) params.set('club', info.club.name)
  return <Navigate to={`/login?${params.toString()}`} replace />
}

/** "el cuestionario <nombre> — <club>", la frase que dice QUÉ le piden al
 *  destinatario y DE PARTE DE QUIÉN. */
function InvitationSummary({ info }: { info: InvitationInfo }) {
  return (
    <>
      Te invitaron a completar <strong>{info.questionnaire_name_es}</strong>
      {info.club ? (
        <>
          {' '}
          de parte de <strong>{info.club.name}</strong>
        </>
      ) : null}{' '}
      en VerSports.
    </>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-surface to-surface-elevated">
      <div className="flex flex-1 items-center justify-center px-lg py-2xl">
        <div className="panel flex max-w-md flex-col gap-md p-xl">
          <VerSportsLogo size="lg" className="self-center" />
          {children}
        </div>
      </div>
      <AboutButton />
    </div>
  )
}
